import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Plus, Users, ChevronRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { api } from '../lib/api';

export function BandsDashboard() {
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const queryClient = useQueryClient();

  const { data: bands = [], isLoading } = useQuery({
    queryKey: ['bands'],
    queryFn: api.bands.list,
  });

  const createBand = useMutation({
    mutationFn: api.bands.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bands'] });
      setShowCreate(false);
      setName('');
      setDescription('');
    },
  });

  return (
    <Layout title="My Bands">
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold">My Bands</h2>
            <p className="text-sm text-gray-400">Select a band to view its library</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            New Band
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : bands.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-4">No bands yet. Create one to get started.</p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg transition-colors text-sm"
            >
              Create Your First Band
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {bands.map((band: any) => (
              <Link key={band.id} href={`/bands/${band.id}`}>
                <div className="flex items-center justify-between p-4 bg-surface-900 border border-surface-700 rounded-xl hover:border-primary-500/50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold group-hover:text-primary-300 transition-colors">{band.name}</h3>
                      {band.description && <p className="text-sm text-gray-400 truncate max-w-xs">{band.description}</p>}
                      <p className="text-xs text-gray-500 capitalize">{band.role}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-primary-400 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Band">
          <form onSubmit={e => { e.preventDefault(); createBand.mutate({ name, description }); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Band Name</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. The Wavelengths"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description (optional)</label>
              <input
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Indie rock band from Portland"
              />
            </div>
            <button
              type="submit"
              disabled={createBand.isPending}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm"
            >
              {createBand.isPending ? 'Creating...' : 'Create Band'}
            </button>
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
